import React, { useState } from 'react';
import { Loader2, AlertCircle, Plus } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { removeCharge } from '@/utils/chargeUtils';
import { addPlan, removePlan } from '@/utils/planUtils';
import { getPlans } from '@/utils/versionUtils';
import { formatChargeName } from './utils/planDisplayUtils';
import { PageScrape } from '@/components/common/ScreenshotModal';
import { ChargeToDelete, CompanyPlansProps, Plan } from './types/planTypes';
import { usePlansData } from './hooks/usePlansData';
import ScreenshotModal from '@/components/common/ScreenshotModal';
import DeleteConfirmationDialog from '@/components/common/DeleteConfirmationDialog';
import AddPlanModal from '@/components/common/AddPlanModal';
import VersionToggle from './VersionToggle';
import PlansTable from './PlansTable';
import ExtractQuarterlyPricesButton from './ExtractQuarterlyPricesButton';
import { Button } from '@/components/ui/button';
import { useGraphQLHelperV2 } from '@/utils/useGraphQLHelperV2';

const CompanyPlans: React.FC<CompanyPlansProps> = ({ slug, subslug }) => {
  const { toast } = useToast();
  
  // Use improved GraphQL helper hook with React Query as single source of truth
  const {
    graphQLData,
    isLoading: graphqlLoading,
    error: graphqlError,
    refetch: refetchGraphQL
  } = useGraphQLHelperV2(slug);
  
  // Use the custom hook to manage plans data
  const {
    plans,
    loading: plansLoading,
    error: plansError,
    actualVersion,
    setActualVersion,
    quarterlyVersions,
    showCurrentOnly,
    setShowCurrentOnly,
    getMostRecentVersionWithPlans,
    allUniquePlans,
    filterPlans,
    isPlanInVersion,
    isChargeInVersion,
    isDiscountInVersion,
    isLegacyPlan,
    fullSlug,
    effectiveGlobalVersion,
    company,
    setPlans,
    versionPlansMap,
    setVersionPlansMap,
    getUniqueChargesForPlan,
    getUniqueDiscountsForPlan
  } = usePlansData(slug, subslug);
  
  // Combine loading states from both hooks
  const loading = graphqlLoading || plansLoading;
  const error = graphqlError || plansError;

  // State for the screenshot modal
  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
  const [selectedImageVersion, setSelectedImageVersion] = useState<string | null>(null);
  const [selectedPlanName, setSelectedPlanName] = useState<string | null>(null);

  // Scrapes for the current page
  const pageScrapes: PageScrape[] = React.useMemo(() => {
    if (!graphQLData?.pages) return [];
    const page = subslug
      ? graphQLData.pages.find(p => p.subslug === subslug)
      : graphQLData.pages.find(p => !p.subslug);
    return page?.scrapes || [];
  }, [graphQLData, subslug]);
  
  // State for the charge delete confirmation dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [chargeToDelete, setChargeToDelete] = useState<ChargeToDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  
  // State for the plan delete confirmation dialog
  const [isPlanDeleteDialogOpen, setIsPlanDeleteDialogOpen] = useState<boolean>(false);
  const [planToDelete, setPlanToDelete] = useState<Plan | null>(null);
  const [isDeletingPlan, setIsDeletingPlan] = useState<boolean>(false);
  
  // State for the add plan modal
  const [isAddPlanModalOpen, setIsAddPlanModalOpen] = useState<boolean>(false);
  const [isAddingPlan, setIsAddingPlan] = useState<boolean>(false);

  // Function to get all plans including those not associated with any versions
  const getAllPlansIncludingNonQuarterly = () => {
    // Start with all unique plans we already have
    const plansSet = new Set(allUniquePlans.map(plan => plan.id));
    const result = [...allUniquePlans];
    
    // Get all plans for the company from the Redux store
    const allCompanyPlans = getAllPlansForCompany();
    
    if (allCompanyPlans && allCompanyPlans.length > 0) {
      // Add any plans that aren't already in our set
      allCompanyPlans.forEach(plan => {
        if (plan.id && !plansSet.has(plan.id)) {
          plansSet.add(plan.id);
          result.push(plan);
        }
      });
    }
    
    // Use the same sorting logic as in allUniquePlans to maintain consistency
    return result.sort((a, b) => {
      // First sort by add-on status
      if ((a.add_on === true) !== (b.add_on === true)) {
        return (a.add_on === true) ? 1 : -1;
      }
      
      // Then sort by price using the same logic as in usePlansData
      const getPlanSortValue = (plan: any): number => {
        // If the plan has no charges, put it at the end
        if (!plan.charges || plan.charges.length === 0) return Infinity;
    
        // Find the lowest price charge that's not pricing_not_disclosed
        const prices = plan.charges
          .filter((charge: any) => charge.type !== 'pricing_not_diclosed')
          .map((charge: any) => charge.price || 0);
    
        // If all charges are pricing_not_disclosed, return Infinity (to put at the end)
        if (prices.length === 0) return Infinity;
    
        // Otherwise, return the lowest price
        return Math.min(...prices);
      };
      
      return getPlanSortValue(a) - getPlanSortValue(b);
    });
  };
  
  // Helper function to get all plans for the company directly from the Redux store
  const getAllPlansForCompany = () => {
    if (!company || !company.pages) {
      return [];
    }
    
    // Collect all plans from all pages
    const allPlans: any[] = [];
    
    company.pages.forEach(page => {
      if (page.plans && Array.isArray(page.plans)) {
        // Only include plans for the current subslug if specified
        if (!subslug || subslug === 'main' || page.slug === subslug) {
          allPlans.push(...page.plans);
        }
      }
    });
    
    return allPlans;
  };

  // Function to open the image modal
  const openImageModal = (version: string, planName: string | null) => {
    setSelectedImageVersion(version);
    setSelectedPlanName(planName);
    setIsImageModalOpen(true);
  };
  
  // Function to open the charge delete confirmation dialog
  const openDeleteDialog = (charge: any) => {
    if (!charge.id) return;
    
    const chargeName = formatChargeName(charge);
    setChargeToDelete({ id: charge.id, name: chargeName });
    setIsDeleteDialogOpen(true);
  };
  
  // Function to open the plan delete confirmation dialog
  const openDeletePlanDialog = (plan: Plan) => {
    if (!plan.id) return;
    
    setPlanToDelete(plan);
    setIsPlanDeleteDialogOpen(true);
  };
  
  // Function to handle charge deletion
  const handleDeleteCharge = async () => {
    if (!chargeToDelete || !chargeToDelete.id) return;
    
    setIsDeleting(true);
    
    try {
      // Step 1: Delete the charge
      const deleteResult = await removeCharge(chargeToDelete.id, slug);
      
      if (deleteResult.success) {
        toast({
          title: "Charge deleted",
          description: `Successfully deleted ${chargeToDelete.name}`,
          variant: "default",
        });
        
        // Find the parent CompanyDetailsPage component's refreshCompanyData function
        const refreshCompanyData = (window as any).refreshCompanyData;
        
        if (typeof refreshCompanyData === 'function') {
          // Use the parent component's refresh function to update all data
          console.log("Calling parent refreshCompanyData function");
          await refreshCompanyData();
        } else {
          // Fallback to manual refresh if parent function not available
          console.log("Parent refreshCompanyData not found, doing manual refresh");
          
          // Step 2: Refresh the main plans data
          console.log("Refreshing main plans data for slug:", fullSlug);
          const mainPlansResult = getPlans(fullSlug);
          console.log("Main plans refresh result:", mainPlansResult);
          
          if (mainPlansResult && mainPlansResult.version && mainPlansResult.data.length > 0) {
            console.log("Setting actual version to:", mainPlansResult.version);
            setActualVersion(mainPlansResult.version);
            console.log("Setting plans to:", mainPlansResult.data);
            setPlans(mainPlansResult.data);
          } else {
            console.log("No plans data returned for main refresh");
          }
          
          // Step 3: Refresh the version plans map
          console.log("Refreshing version plans map for quarterly versions");
          const newPlansMap = new Map(versionPlansMap);
          
          for (const version of quarterlyVersions) {
            console.log("Refreshing plans for version:", version);
            const versionResult = getPlans(fullSlug, version);
            
            if (versionResult && versionResult.version && versionResult.data.length > 0) {
              console.log("Adding version to map:", version, "with", versionResult.data.length, "plans");
              newPlansMap.set(version, versionResult.data);
            } else {
              console.log("No plans data returned for version:", version);
            }
          }
          
          console.log("Setting version plans map with", newPlansMap.size, "entries");
          setVersionPlansMap(newPlansMap);
        }
      } else {
        toast({
          title: "Error",
          description: deleteResult.message || "Failed to delete charge",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting charge:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting the charge",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setChargeToDelete(null);
    }
  };
  
  // Function to handle plan deletion
  const handleDeletePlan = async () => {
    if (!planToDelete || !planToDelete.id) return;
    
    setIsDeletingPlan(true);
    
    try {
      // Delete the plan
      const deleteResult = await removePlan(planToDelete.id, slug);
      
      if (deleteResult.success) {
        toast({
          title: "Plan deleted",
          description: `Successfully deleted ${planToDelete.display_name || planToDelete.plan_name}`,
          variant: "default",
        });
        
        // Find the parent CompanyDetailsPage component's refreshCompanyData function
        const refreshCompanyData = (window as any).refreshCompanyData;
        
        if (typeof refreshCompanyData === 'function') {
          // Use the parent component's refresh function to update all data
          console.log("Calling parent refreshCompanyData function");
          await refreshCompanyData();
        } else {
          // Fallback to manual refresh if parent function not available
          console.log("Parent refreshCompanyData not found, doing manual refresh");
          
          // Refresh the main plans data
          console.log("Refreshing main plans data for slug:", fullSlug);
          const mainPlansResult = getPlans(fullSlug);
          
          if (mainPlansResult && mainPlansResult.version && mainPlansResult.data.length > 0) {
            setActualVersion(mainPlansResult.version);
            setPlans(mainPlansResult.data);
          } else {
            console.log("No plans data returned for main refresh");
          }
          
          // Refresh the version plans map
          const newPlansMap = new Map(versionPlansMap);
          
          for (const version of quarterlyVersions) {
            const versionResult = getPlans(fullSlug, version);
            
            if (versionResult && versionResult.version && versionResult.data.length > 0) {
              newPlansMap.set(version, versionResult.data);
            }
          }
          
          setVersionPlansMap(newPlansMap);
        }
      } else {
        if (deleteResult.hasCharges) {
          toast({
            title: "Cannot Delete Plan",
            description: deleteResult.message || "This plan has associated charges. Please delete all charges first.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: deleteResult.message || "Failed to delete plan",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting the plan",
        variant: "destructive",
      });
    } finally {
      setIsDeletingPlan(false);
      setIsPlanDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  // Handle toggle change for showing current plans only
  const handleToggleChange = (checked: boolean) => {
    // Only allow switching to "Show Current Plans Only" if there are plans to show
    if (!checked || filterPlans(allUniquePlans).length > 0) {
      setShowCurrentOnly(checked);
    } else {
      // If trying to switch to current-only but no plans would be shown, keep it at "Show All Plans"
      toast({
        title: "No current plans available",
        description: "Showing all plans because no plans exist in the current version.",
        duration: 3000,
      });
    }
  };
  
  // Function to handle adding a new plan
  const handleAddPlan = async (planName: string, displayName: string, isAddOn: boolean) => {
    if (!company || !company.pages || !company.pages.length) {
      toast({
        title: "Error",
        description: "Cannot add plan: company or page information is missing",
        variant: "destructive",
      });
      return;
    }
    
    // Find the current page ID based on subslug
    let currentPage = company.pages.find(page => 
      page.slug === subslug || (!subslug && page.slug === 'main')
    );
    
    // If no page is found based on subslug, use the first page as fallback
    if (!currentPage || !currentPage.id) {
      console.log("Page not found based on subslug, using first page as fallback");
      currentPage = company.pages[0];
      
      // If still no valid page, show error
      if (!currentPage || !currentPage.id) {
        toast({
          title: "Error",
          description: "Cannot add plan: page information is missing",
          variant: "destructive",
        });
        return;
      }
      
      // Log which page we're using as fallback
      console.log(`Using fallback page: ${currentPage.slug} (ID: ${currentPage.id})`);
    }
    
    setIsAddingPlan(true);
    
    try {
      // Call the utility function to add the plan
      const result = await addPlan(
        currentPage.id,
        planName,
        displayName,
        isAddOn
      );
      
      if (result.success) {
        toast({
          title: "Plan added",
          description: `Successfully added plan "${planName}"`,
          variant: "default",
        });
        
        // Find the parent CompanyDetailsPage component's refreshCompanyData function
        const refreshCompanyData = (window as any).refreshCompanyData;
        
        if (typeof refreshCompanyData === 'function') {
          // Use the parent component's refresh function to update all data
          console.log("Calling parent refreshCompanyData function");
          await refreshCompanyData();
        } else {
          // Fallback to manual refresh if parent function not available
          console.log("Parent refreshCompanyData not found, doing manual refresh");
          const mainPlansResult = getPlans(fullSlug);
          
          if (mainPlansResult && mainPlansResult.version && mainPlansResult.data.length > 0) {
            setActualVersion(mainPlansResult.version);
            setPlans(mainPlansResult.data);
          }
        }
        
        // Close the modal
        setIsAddPlanModalOpen(false);
      } else {
        console.error('Failed to add plan:', result);
        toast({
          title: "Error",
          description: result.message || "Failed to add plan",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error adding plan:', error);
      // Provide more detailed error information
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : "An unexpected error occurred";
          
      toast({
        title: "Error",
        description: `Failed to add plan: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsAddingPlan(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-500">Loading plans data...</p>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="mt-6 grid place-items-center">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error instanceof Error ? error.message : String(error)}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Plans Content (both empty and populated states) */}
      {!loading && !error && (
        <div>
          {/* Message for empty state */}
          {allUniquePlans.length === 0 && (
            <div className="mb-6">
              <p className="text-gray-500 mb-1">No plans data available for this company.</p>
              <p className="text-sm text-gray-400">Use the Run Extract button above to extract plans data from the pricing page.</p>
            </div>
          )}

          {/* Header with Version Toggle and Action Buttons */}
          <div className="flex justify-between items-center mb-4">
            <VersionToggle 
              showCurrentOnly={showCurrentOnly}
              onToggleChange={handleToggleChange}
              slug={slug}
              subslug={subslug}
              actualVersion={actualVersion}
              getMostRecentVersionWithPlans={getMostRecentVersionWithPlans}
            />
            
            <div className="flex space-x-2">
              <ExtractQuarterlyPricesButton
                slug={slug}
                subslug={subslug}
                variant="outline"
                size="sm"
              />
              
              <Button 
                onClick={() => setIsAddPlanModalOpen(true)}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Plan
              </Button>
            </div>
          </div>
          
          {/* Plans Table - Show either real plans or empty placeholder */}
          {allUniquePlans.length > 0 ? (
            <PlansTable
              plans={(showCurrentOnly ? filterPlans(allUniquePlans) : getAllPlansIncludingNonQuarterly())}
              quarterlyVersions={quarterlyVersions}
              openImageModal={openImageModal}
              openDeletePlanDialog={openDeletePlanDialog}
              isLegacyPlan={isLegacyPlan}
              showCurrentOnly={showCurrentOnly}
              subslug={subslug}
              company={company}
              activePageId={company?.pages?.[0]?.id || company?.pages?.[0]?.page_id || null}
              allScrapes={company?.pages?.[0]?.scrapes || null}
            />
          ) : (
            <PlansTable
              plans={[{
                id: 'empty-plan',
                plan_name: 'No plans available',
                display_name: 'No plans available',
                add_on: false,
                charges: [{
                  id: 'empty-charge',
                  type: 'empty',
                  currency: null,
                  price: null,
                  price_per: null,
                  notes: 'No pricing data available'
                }]
              }]}
              quarterlyVersions={quarterlyVersions}
              openImageModal={openImageModal}
              openDeletePlanDialog={() => {}}
              isLegacyPlan={() => false}
              showCurrentOnly={showCurrentOnly}
            />
          )}

          {/* Debug info for empty state */}
          {allUniquePlans.length === 0 && (
            <details className="mt-4">
              <summary className="text-xs text-gray-500 cursor-pointer">Debug information</summary>
              <div className="p-4 bg-gray-100 rounded text-left mt-2">
                <p className="text-xs text-gray-600 font-mono">Slug: {slug}</p>
                <p className="text-xs text-gray-600 font-mono">Subslug: {subslug || 'none'}</p>
                <p className="text-xs text-gray-600 font-mono">Full slug: {fullSlug}</p>
                <p className="text-xs text-gray-600 font-mono">Requested version: {effectiveGlobalVersion || 'none'}</p>
                <p className="text-xs text-gray-600 font-mono">Actual version: {actualVersion || 'none'}</p>
                <p className="text-xs text-gray-600 font-mono">Quarterly versions: {quarterlyVersions.join(', ') || 'none'}</p>
              </div>
            </details>
          )}
        </div>
      )}

      {/* Screenshot Modal */}
      <ScreenshotModal
        isOpen={isImageModalOpen}
        onOpenChange={setIsImageModalOpen}
        scrapes={pageScrapes}
        initialVersion={selectedImageVersion}
        title={`${selectedPlanName || 'Plan'} - ${slug}${subslug ? `/${subslug}` : ''}`}
      />
      
      {/* Charge Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteCharge}
        title="Delete Charge"
        description={`Are you sure you want to delete the charge "${chargeToDelete?.name || 'this charge'}"? This action cannot be undone and will remove the charge from all versions.`}
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
      />
      
      {/* Plan Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isPlanDeleteDialogOpen}
        onOpenChange={setIsPlanDeleteDialogOpen}
        onConfirm={handleDeletePlan}
        title="Delete Plan"
        description={`Are you sure you want to delete the plan "${planToDelete?.display_name || planToDelete?.plan_name || 'this plan'}"? This action cannot be undone and will remove the plan from all versions.`}
        confirmLabel={isDeletingPlan ? "Deleting..." : "Delete"}
      />
      
      {/* Add Plan Modal */}
      <AddPlanModal
        isOpen={isAddPlanModalOpen}
        onOpenChange={setIsAddPlanModalOpen}
        onConfirm={handleAddPlan}
        isSubmitting={isAddingPlan}
      />
    </div>
  );
};

export default CompanyPlans;
